import { DbRef, Role } from '@caresend/types';
import 'immutability-helper';
import { arrayToObj } from '../languageExtensions/array.js';
import 'lodash-unified';
import '../typeValidation/booking.js';
import '../typeValidation/draftWaypoint.js';
import '../typeValidation/feedback.js';
import '../environment/crypto/index.js';
import 'dayjs';
import '../formatters/string.js';
import 'ngeohash';
import '../formatters/location.js';
import '../initializers/user.js';
import '../typeValidation/note.js';
import '../typeValidation/procedure.js';
import '../typeValidation/samples.js';
import '../typeValidation/shift.js';
import '../typeValidation/shipmentInfo.js';
import '../typeValidation/supply.js';
import '../typeValidation/timeSlot.js';
import '../typeValidation/waypoint.js';
import '../typeValidation/waypointAction.js';
import { shouldEncryptProcedureContact } from '../dataHelpers/procedure.js';
import '../dataHelpers/product.js';
import 'libphonenumber-js';
import '../dataHelpers/supplyBoxPackerHelper.js';
import { shouldEncryptSchedulingUserName } from '../dataHelpers/user.js';
import { maybeEncryptValue, encryptValue, maybeDecryptValue } from './helpers.js';
import { fetchWithRelationships } from '../relationships/index.js';

/**
 * Gets the access attributes for a draft waypoint.
 */

const getDraftWaypointAccessAttributes = async draftWaypointID => {
  var _a;

  const {
    orders,
    procedures,
    users
  } = await fetchWithRelationships(DbRef.DRAFT_WAYPOINTS, draftWaypointID, {
    procedures: {
      bookingID: {
        patientID: true
      },
      orderID: true
    }
  });
  const order = Object.values(orders !== null && orders !== void 0 ? orders : {})[0];
  const {
    schedulingUserID
  } = order !== null && order !== void 0 ? order : {};
  const {
    contact
  } = (_a = Object.values(procedures !== null && procedures !== void 0 ? procedures : {})[0]) !== null && _a !== void 0 ? _a : {};
  return [{
    role: Role.ASSISTANT
  }, {
    role: Role.PRESCRIBER
  }, {
    userID: schedulingUserID !== null && schedulingUserID !== void 0 ? schedulingUserID : contact === null || contact === void 0 ? void 0 : contact.userID
  }, ...Object.values(users !== null && users !== void 0 ? users : {}).map(user => ({
    userID: user.id
  }))];
};
const encryptDraftWaypointFields = async (draftWaypoint, accessAttributes) => {
  const {
    contact: procedureContact,
    finalizedByUserID,
    location,
    schedulingUserName
  } = draftWaypoint;
  const [contactMaybeEncrypted, finalizedByUserIDEncrypted, locationEncrypted, schedulingUserNameEncrypted] = await Promise.all([maybeEncryptValue(procedureContact, accessAttributes, shouldEncryptProcedureContact), encryptValue(finalizedByUserID, accessAttributes), encryptValue(location, accessAttributes), maybeEncryptValue(schedulingUserName, accessAttributes, shouldEncryptSchedulingUserName)]);
  return {
    contact: contactMaybeEncrypted,
    finalizedByUserID: finalizedByUserIDEncrypted,
    location: locationEncrypted,
    schedulingUserName: schedulingUserNameEncrypted
  };
};
const encryptDraftWaypoint = async (draftWaypoint, accessAttributes) => {
  const {
    contact,
    finalizedByUserID,
    location,
    schedulingUserName
  } = await encryptDraftWaypointFields(draftWaypoint, accessAttributes);
  return Object.assign(Object.assign({}, draftWaypoint), {
    contact,
    finalizedByUserID,
    location,
    schedulingUserName
  });
};
const decryptDraftWaypoint = async draftWaypoint => {
  const {
    contact,
    finalizedByUserID,
    location,
    schedulingUserName
  } = draftWaypoint;
  const [locationDecrypted, contactDecrypted, finalizedByUserIDDecrypted, schedulingUserNameDecrypted] = await Promise.all([maybeDecryptValue(location), maybeDecryptValue(contact), maybeDecryptValue(finalizedByUserID), maybeDecryptValue(schedulingUserName)]);
  return Object.assign(Object.assign({}, draftWaypoint), {
    contact: contactDecrypted,
    finalizedByUserID: finalizedByUserIDDecrypted,
    location: locationDecrypted,
    schedulingUserName: schedulingUserNameDecrypted
  });
};
const decryptDraftWaypoints = async draftWaypoints => {
  const decryptedDraftWaypoints = await Promise.all(Object.values(draftWaypoints).map(decryptDraftWaypoint));
  return arrayToObj(decryptedDraftWaypoints, 'id');
};
/**
 * Determines if a value at a given path should be encrypted in the database.
 * @param childPath - The path to the value.
 * @param value - The value to check.
 * @returns True if the value should be encrypted, false otherwise.
 */

const shouldEncryptDraftWaypointField = (childPath, value) => {
  switch (childPath) {
    case 'contact':
      return shouldEncryptProcedureContact(value);

    case 'finalizedByUserID':
      return true;

    case 'location':
      return true;

    case 'schedulingUserName':
      return shouldEncryptSchedulingUserName(value);

    default:
      return false;
  }
};

export { decryptDraftWaypoint, decryptDraftWaypoints, encryptDraftWaypoint, encryptDraftWaypointFields, getDraftWaypointAccessAttributes, shouldEncryptDraftWaypointField };
